import {
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import React, { ReactNode, useEffect, useState } from "react";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";

type Props = {
  value: string;
  errorMessage: ReactNode;
  label: string;
  name: string;
  placeholder: string;
  maxLength?: number;
  handleInputChange: (field: string, value: string) => void;
  multiline?: boolean;
  isError: boolean;
  setErrorHandler?: (field: string, value: boolean) => void;
  isPassword?: boolean;
  isDisabled?: boolean;
  noteText?: string;
  maxRows?: number;
  withResize?: boolean;
  minHeight?: string;
  sx?: SxProps<Theme>;
};

export const ReuseInput = ({
  value,
  label,
  name,
  placeholder,
  maxLength,
  errorMessage,
  handleInputChange,
  multiline,
  isError,
  setErrorHandler,
  isPassword,
  isDisabled,
  noteText,
  sx,
  maxRows = 4,
  withResize = true,
  minHeight = "100px",
}: Props) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    setErrorHandler &&
      setErrorHandler(
        name,
        isError || (!!maxLength && value.length > maxLength),
      );
  }, [value, isError]);

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <TextField
        type={isPassword ? (showPassword ? "text" : "password") : "text"}
        fullWidth
        name={name}
        placeholder={placeholder}
        label={label}
        value={value}
        variant="outlined"
        error={isError}
        onChange={(e) => handleInputChange(name, e.target.value)}
        multiline={multiline}
        disabled={isDisabled}
        maxRows={maxRows}
        className={"scrollable_container"}
        sx={{
          textarea: {
            minHeight,
            resize: withResize ? "vertical" : "none",
            scrollbarWidth: "thin",

            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
            maxHeight: "Calc(100vh - 500px)",
            "&::placeholder": {
              color: "#9e9e9e",
            },
          },
          "& .MuiInputLabel-root": {
            color: isError ? "#D32F2F" : "#9e9e9e",
            "&.Mui-focused": {
              color: isError ? "#D32F2F" : "#9e9e9e",
            },
          },

          borderColor: isError ? "#D32F2F" : isDisabled ? "#CFCFCF" : "auto",
          "& .MuiOutlinedInput-root": {
            borderColor: isError ? "#D32F2F" : isDisabled ? "#CFCFCF" : "auto",
            "&:hover": {
              borderColor: isError
                ? "#D32F2F"
                : isDisabled
                  ? "#CFCFCF"
                  : "auto",
            },

            "&.Mui-focused": {
              borderColor: isError
                ? "#D32F2F"
                : isDisabled
                  ? "#CFCFCF"
                  : "auto",
            },
          },
          ...sx,
        }}
        InputProps={{
          endAdornment: isPassword ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <VisibilityOffOutlined />
                ) : (
                  <VisibilityOutlined />
                )}
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <Box position="relative">
        <FormHelperText
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            fontSize: "12px",
            lineHeight: "16px",
            color: isError ? "#D32F2F" : "#00000099",
          }}
        >
          <span style={{ width: "100%" }}>
            {isError ? errorMessage : noteText}
          </span>
          {!!maxLength && <span>{`${value?.length}/${maxLength}`}</span>}
        </FormHelperText>
      </Box>
    </Box>
  );
};
